import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import { ShoppingCart, Users, FileSpreadsheet, PieChart, PackageCheck, RefreshCw } from 'lucide-react';
import { MarketingShell, PageHero, PageSection, CardGrid, FaqList, ClosingCta, buildFaqJsonLd, buildBreadcrumb } from '@/components/marketing/MarketingShell';

const FAQ = [
  {
    q: 'Apa bedanya CRM Nerava dengan catatan di spreadsheet?',
    a: 'Data pelanggan dan pesanan di Nerava terbentuk langsung dari percakapan, jadi tidak ada tahap salin tempel. Setiap pesanan terhubung ke riwayat chat, bukti transfer, status pengiriman, dan invoice-nya.',
  },
  {
    q: 'Apakah stok berkurang otomatis saat ada pesanan?',
    a: 'Ya. Stok berkurang saat pesanan dibuat dan kembali lagi saat pesanan dibatalkan, sehingga catatan stok tetap sesuai dengan barang di gudang.',
  },
  {
    q: 'Bisakah melihat pelanggan yang sering belanja?',
    a: 'Bisa. Setiap pelanggan punya halaman berisi total belanja, jumlah pesanan, produk favorit, dan kapan terakhir berinteraksi, jadi mudah menentukan siapa yang layak diprioritaskan.',
  },
  {
    q: 'Apakah invoice dibuat otomatis?',
    a: 'Invoice dibuat otomatis begitu pesanan dikonfirmasi, lengkap dengan rincian produk, ongkir, dan total. Bisa dikirim langsung ke pembeli lewat chat.',
  },
];

const JSON_LD = [buildBreadcrumb('CRM & Pesanan', '/crm-pesanan'), buildFaqJsonLd(FAQ)];

const FEATURES = [
  { icon: <ShoppingCart className="w-5 h-5" />, title: 'Pesanan dari chat', description: 'Pesanan terbentuk langsung dari percakapan, lengkap dengan produk, varian, ongkir, dan alamat pengiriman.' },
  { icon: <Users className="w-5 h-5" />, title: 'Database pelanggan', description: 'Riwayat belanja, total spending, dan produk favorit tiap pelanggan tersimpan rapi tanpa input manual.' },
  { icon: <PackageCheck className="w-5 h-5" />, title: 'Stok selalu akurat', description: 'Stok berkurang saat pesanan dibuat dan kembali saat dibatalkan, jadi tidak ada lagi jual barang yang sudah habis.' },
  { icon: <FileSpreadsheet className="w-5 h-5" />, title: 'Invoice otomatis', description: 'Invoice rapi dibuat begitu pesanan dikonfirmasi dan bisa langsung dikirim ke pembeli lewat chat.' },
  { icon: <PieChart className="w-5 h-5" />, title: 'Laporan penjualan', description: 'Omzet, produk terlaris, dan performa channel terlihat dalam satu layar, tanpa menyusun laporan manual.' },
  { icon: <RefreshCw className="w-5 h-5" />, title: 'Follow up repeat order', description: 'Pelanggan lama diingatkan untuk belanja lagi dengan pesan yang Anda atur sendiri jadwal dan isinya.' },
];

const CrmOrdersPage = () => (
  <MarketingShell active="/crm-pesanan">
    <SEOHead
      title="CRM & Manajemen Pesanan untuk Toko Online | Nerava"
      description="Kelola pelanggan, pesanan, stok, dan invoice dalam satu tempat. Data terbentuk langsung dari chat WhatsApp, Instagram & Messenger, tanpa salin tempel. Coba gratis 30 hari."
      canonical="https://nerava.id/crm-pesanan"
      jsonLd={JSON_LD}
    />
    <PageHero
      eyebrow="CRM & Pesanan"
      title="Catatan pelanggan dan pesanan"
      highlight="rapi dengan sendirinya"
      subtitle="Setiap chat yang berujung pesanan langsung menjadi data: pembeli, produk, stok, bukti transfer, dan invoice. Tidak ada lagi rekap manual tiap malam."
      bullets={['Tanpa salin tempel', 'Stok ikut terjaga', 'Invoice otomatis']}
    />

    <PageSection title="Dari percakapan jadi data yang bisa dipakai" intro="Nerava merapikan bagian yang paling memakan waktu: mencatat, mengecek, dan mengingat." tinted>
      <CardGrid items={FEATURES} />
    </PageSection>

    <PageSection title="Alur pesanan di Nerava">
      <ol className="space-y-4 max-w-3xl">
        {[
          ['Pembeli bertanya', 'AI menjawab dari katalog dan mencatat minat pembeli di profil pelanggan.'],
          ['Pesanan dibuat', 'Varian, jumlah, dan ongkir dikunci, lalu stok langsung menyesuaikan.'],
          ['Pembayaran diverifikasi', 'Bukti transfer dibaca dan dicocokkan dengan tagihan sebelum status berubah.'],
          ['Pengiriman dan laporan', 'Status pengiriman diperbarui dan angkanya masuk ke laporan penjualan.'],
        ].map(([title, desc], i) => (
          <li key={title} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900/60">
            <span className="grid place-items-center w-9 h-9 flex-shrink-0 rounded-full bg-indigo-600 text-white text-sm font-semibold">{i + 1}</span>
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1">{title}</h3>
              <p className="text-sm text-slate-600 dark:text-zinc-400">{desc}</p>
            </div>
          </li>
        ))}
      </ol>
      <p className="mt-8 text-sm text-slate-500 dark:text-zinc-400">
        Bagian yang menjawab pembeli dijelaskan di{' '}
        <Link to="/ai-customer-service" className="text-indigo-600 dark:text-indigo-400 hover:underline" data-testid="link-to-acs-from-crm">
          AI Customer Service
        </Link>
        , dan rincian paketnya ada di{' '}
        <Link to="/harga" className="text-indigo-600 dark:text-indigo-400 hover:underline" data-testid="link-to-pricing-from-crm">
          halaman harga
        </Link>
        .
      </p>
    </PageSection>

    <PageSection title="Pertanyaan soal CRM dan pesanan" tinted>
      <FaqList items={FAQ} />
    </PageSection>

    <ClosingCta
      title="Berhenti rekap pesanan tengah malam"
      subtitle="Coba 30 hari gratis dan biarkan pesanan, stok, serta invoice tercatat sendiri dari percakapan."
    />
  </MarketingShell>
);

export default CrmOrdersPage;
